/**
 * Klagenfurt P0 2026-08 (Review Point 2 correction) —
 * RentalComparablesModal
 *
 * Analog to ``ComparablesModal`` but for the RENT comparables that the
 * yield calculation actually used. Opened from the RentEstimateBlock
 * "Mietvergleichsobjekte ansehen" button.
 *
 * Rules from user brief B / C / G:
 *   * Only the rentals that contributed to THIS deal's yield.
 *   * Cards navigate INTERNALLY to `/listing/{id}` — the external
 *     source link is a small secondary action.
 *   * Excluded records are shown in an OPT-IN collapsed section with
 *     the exclusion reason. They never contribute to any statistic.
 *   * All labels via i18n (market.rentalComparables.*).
 */
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, ChevronUp, ExternalLink, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getDealComparables } from "../api/dealflowApi";
import { saveAdvisorReturnState } from "../utils/advisorReturnStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

function fmtDistance(t, m) {
  if (m == null) return null;
  if (m < 1000) return t("comparables.distanceM", { m });
  return t("comparables.distanceKm", {
    km: (m / 1000).toFixed(1).replace(".", ","),
  });
}

function RentRow({ comp, t, onOpen }) {
  const inactive = comp.status && comp.status !== "active";
  const distance = fmtDistance(t, comp.distance_m);
  const meta = [
    comp.living_area_m2 ? `${Math.round(comp.living_area_m2)} m²` : null,
    comp.rooms ? t("comparables.rooms", { count: comp.rooms }) : null,
    [comp.postal_code].filter(Boolean).join(" ") || null,
  ]
    .filter(Boolean)
    .join(" · ");
  const rentDisplay =
    comp.rent_display ||
    (comp.rent_monthly != null
      ? t("rentalComparables.rentPerMonth", {
          value: Math.round(comp.rent_monthly).toLocaleString("de-AT"),
        })
      : "—");
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => comp.id && onOpen(comp.id)}
      onKeyDown={(e) => e.key === "Enter" && comp.id && onOpen(comp.id)}
      data-testid={`rental-comparable-row-${comp.id}`}
      className="flex gap-3 rounded-xl border border-zinc-800/80 bg-zinc-900/40 p-2.5 hover:border-emerald-500/40 transition-colors group cursor-pointer"
    >
      <div className="w-20 h-16 shrink-0 rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800">
        {comp.image ? (
          <img
            src={comp.image}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-700 text-lg">
            ◾
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xs text-zinc-300 line-clamp-1">
            {comp.title || "—"}
          </div>
          {comp.url ? (
            <a
              href={comp.url}
              target="_blank"
              rel="noreferrer"
              title={t("comparables.openOriginal")}
              data-testid={`rental-comparable-external-${comp.id}`}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0"
            >
              <ExternalLink className="h-3.5 w-3.5 text-zinc-600 hover:text-emerald-300 transition-colors" />
            </a>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="text-sm font-semibold text-emerald-300">
            {rentDisplay}
            {comp.rent_per_m2 != null ? (
              <span className="text-[11px] font-normal text-zinc-500 ml-2">
                {t("rentalComparables.rentPerM2", {
                  value: (comp.rent_per_m2).toLocaleString("de-AT", {
                    maximumFractionDigits: 1,
                  }),
                })}
              </span>
            ) : null}
          </div>
          {distance && (
            <span
              data-testid={`rental-comparable-distance-${comp.id}`}
              className="inline-flex items-center gap-1 shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-300"
            >
              <MapPin className="h-3 w-3" />
              {distance}
            </span>
          )}
        </div>
        {meta && (
          <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-1">
            {meta}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-1">
          <span className="inline-flex items-center gap-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-px text-[10px] text-emerald-300">
            <Check className="h-2.5 w-2.5" />
            {t("rentalComparables.consideredBadge")}
          </span>
          {Array.isArray(comp.match_reasons) &&
            comp.match_reasons.slice(0, 3).map((slug) => (
              <span
                key={slug}
                className="inline-flex items-center gap-0.5 rounded-full border border-zinc-700/60 bg-zinc-800/60 px-1.5 py-px text-[10px] text-zinc-400"
              >
                {t(`rentalComparables.matchReasons.${slug}`, {
                  defaultValue: slug,
                })}
              </span>
            ))}
        </div>
        {inactive && (
          <div className="text-[10px] text-amber-400/70 mt-0.5">
            {t("comparables.inactive")}
          </div>
        )}
      </div>
    </div>
  );
}

function ExcludedRow({ x, t }) {
  return (
    <div
      data-testid={`rental-comparable-excluded-${x.id}`}
      className="flex flex-col gap-1 rounded-xl border border-red-900/60 bg-red-950/25 px-3 py-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-red-200">
          {t(`rentalComparables.reasons.${x.reason}`, {
            defaultValue: x.reason_label || x.reason,
          })}
        </span>
        {x.rent_monthly != null && (
          <span className="text-xs text-red-300 tabular-nums">
            {t("rentalComparables.rentPerMonth", {
              value: Math.round(x.rent_monthly).toLocaleString("de-AT"),
            })}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-zinc-400">
        {x.rent_per_m2 != null && (
          <span>
            {t("rentalComparables.rentPerM2", {
              value: (x.rent_per_m2).toLocaleString("de-AT", {
                maximumFractionDigits: 1,
              }),
            })}
          </span>
        )}
        {x.living_area_m2 != null && (
          <span>{Math.round(x.living_area_m2)} m²</span>
        )}
        {x.postal_code && <span>PLZ {x.postal_code}</span>}
      </div>
    </div>
  );
}

export default function RentalComparablesModal({ ulid, open, onOpenChange }) {
  const { t, i18n } = useTranslation("market");
  const navigate = useNavigate();
  const lang = (i18n.language || "de").slice(0, 2).toLowerCase();
  const [showExcluded, setShowExcluded] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["deal-comparables", ulid, lang],
    queryFn: () => getDealComparables(ulid, lang),
    enabled: open && !!ulid,
    staleTime: 60 * 1000,
  });

  const rental = data?.rental_comparables;
  const items = useMemo(() => rental?.items || [], [rental]);
  const excluded = useMemo(
    () => (data?.excluded_comparables || []).filter((x) => x.category === "rental"),
    [data],
  );
  // Klagenfurt P0.4c Punkt 1 — legacy record without a persisted
  // ULID basis. NEVER claim the current live cascade was the original
  // one; render the "must be recomputed" notice instead of items.
  const isLegacyNoBasis =
    data && rental && rental.legacy_no_basis === true;
  const basisIsExact = data?.basis_is_exact === true;

  function openListing(id) {
    if (!id) return;
    // P0.4c Punkt 3 — snapshot state so a BACK-navigation from the
    // comparable's page restores the source listing + advisor panel.
    // We don't know for certain whether the panel is open, but the
    // modal itself is a listing-page surface — flag `wasOpen=false`
    // so ListingDetailPage only restores scroll (not force-open the
    // advisor unless it was actually open, which the panel decides
    // from its own conversation state).
    saveAdvisorReturnState(ulid, { conversationId: null, wasOpen: false });
    onOpenChange(false);
    navigate(`/listing/${id}`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="rental-comparables-modal"
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-950 border-zinc-800"
      >
        <DialogHeader>
          <DialogTitle className="text-emerald-200">
            {t("rentalComparables.title")}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {t("rentalComparables.subtitle")}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="py-8 text-center text-zinc-500 text-sm">
            {t("comparables.loading")}
          </div>
        )}
        {isError && (
          <div className="py-6 text-center text-red-300 text-sm">
            {t("comparables.error")}
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {isLegacyNoBasis ? (
              <div
                data-testid="rental-comparables-legacy-notice"
                className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 mt-2"
              >
                {t("rentalComparables.legacyEmpty")}
              </div>
            ) : items.length === 0 ? (
              <div
                data-testid="rental-comparables-empty"
                className="py-6 text-center text-zinc-500 text-sm"
              >
                {t("rentalComparables.empty")}
              </div>
            ) : (
              <>
                {basisIsExact && data?.basis_calculated_at && (
                  <div
                    data-testid="rental-comparables-basis-stamp"
                    className="text-[10px] text-zinc-500 mt-1"
                  >
                    {t("rentalComparables.basisCalculatedAt", {
                      date: new Date(data.basis_calculated_at)
                        .toLocaleDateString(lang, {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                        }),
                    })}
                  </div>
                )}
                <div className="space-y-2 mt-2">
                  {items.map((c) => (
                    <RentRow
                      key={c.ulid || c.id}
                      comp={c}
                      t={t}
                      onOpen={openListing}
                    />
                  ))}
                </div>
              </>
            )}

            {excluded.length > 0 && (
              <div className="mt-4">
                <button
                  type="button"
                  data-testid="rental-comparables-excluded-toggle"
                  onClick={() => setShowExcluded((v) => !v)}
                  className="flex items-center gap-1.5 w-full justify-between px-3 py-2 rounded-lg border border-zinc-800 bg-zinc-900/60 hover:border-red-500/40 text-xs text-zinc-300"
                >
                  <span>
                    {showExcluded
                      ? t("rentalComparables.excludedHide")
                      : t("rentalComparables.excludedShow", {
                          count: excluded.length,
                        })}
                  </span>
                  {showExcluded ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
                {showExcluded && (
                  <div className="mt-2 space-y-2">
                    <div className="text-[11px] text-zinc-500">
                      {t("rentalComparables.excludedSubtitle")}
                    </div>
                    {excluded.map((x, idx) => (
                      <ExcludedRow key={`${x.id}-${idx}`} x={x} t={t} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
